import React from 'react'

import { AutomatedStatus } from '@/components/atoms/AutomatedStatus/AutomatedStatus.tsx'
import { CancelledStatus } from '@/components/atoms/CancelledStatus/CancelledStatus.tsx'
import { Car } from '@/components/atoms/Car/Car.tsx'
import { Dialog, DialogContent, DialogTrigger } from '@/components/atoms/Dialog/Dialog.tsx'
import { Donation } from '@/components/atoms/Donation/Donation.tsx'
import { Icons } from '@/components/atoms/Icons/Icons.tsx'
import { MobileState } from '@/components/atoms/MobileState/MobileState.tsx'
import { Pin } from '@/components/atoms/Pin/Pin.tsx'
import { Status } from '@/components/molecules/Status/Status.tsx'
import { cn } from '@/lib/utils.ts'

export interface InformationButtonLabels {
  legend: {
    team: {
      title: string
      available: string
      assigned: string
      allocated: string
      onboarded: string
      unfindable: string
      paused: string
      returning: string
      onboardFlagged: string
    }
    request: {
      title: string
      pending: string
      assigned: string
      allocated: string
      onboarded: string
      unfindable: string
      completed: string
      cancelled: string
      mobile: string
    }
    donation: {
      title: string
      no: string
      cash: string
      digital: string
      textSent: string
      textClicked: string
    }
    teamMobileStatus: {
      title: string
      noMobile: string
      mobile: string
      notConnected: string
    }
    carVariations: {
      title: string
      vip: string
      electric: string
      manual: string
    }
    automation: {
      title: string
      active: string
      pending: string
      inactive: string
    }
    requestCardState: {
      pending: string
      automated: string
      assigned: string
      allocated: string
      onboarded: string
    }
  }
}
export interface InformationButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  labels: InformationButtonLabels
}

const carNumber = '3'
const letter = 'A'
const letterCompleted = 'B'

export const InformationButton = React.forwardRef<HTMLButtonElement, InformationButtonProps>(
  ({ labels, className, ...props }, ref) => (
    <Dialog>
      <DialogTrigger asChild>
        <button
          type="button"
          className={cn('group flex h-10 w-10 items-center justify-center bg-gray-800', className)}
          ref={ref}
          {...props}>
          <Icons.information className="size-5 fill-orange-300 group-hover:fill-orange-200" />
        </button>
      </DialogTrigger>
      <DialogContent className="rounded-corner sm:rounded-corner top-[50%] flex max-w-6xl flex-col gap-6 px-14 py-16">
        <main className="flex flex-wrap justify-between gap-3 select-none">
          <div className="max-w-[180px] space-y-5">
            <p className="text-base font-bold text-black">{labels.legend.team.title}</p>
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-3">
                <Car state="available" carNumber={carNumber} />
                <p className="mb-1 text-sm font-normal text-gray-800">{labels.legend.team.available}</p>
              </div>
              <div className="flex items-center gap-3">
                <Car state="assigned" carNumber={carNumber} />
                <p className="mb-1 text-sm font-normal text-gray-800">{labels.legend.team.assigned}</p>
              </div>
              <div className="flex items-center gap-3">
                <Car state="allocated" carNumber={carNumber} />
                <p className="mb-1 text-sm font-normal text-gray-800">{labels.legend.team.allocated}</p>
              </div>
              <div className="flex items-center gap-3">
                <Car state="onboarded" carNumber={carNumber} />
                <p className="mb-1 text-sm font-normal text-gray-800">{labels.legend.team.onboarded}</p>
              </div>
              <div className="flex items-center gap-3">
                <Car state="unfindabled" carNumber={carNumber} />
                <p className="mb-1 text-sm font-normal text-gray-800">{labels.legend.team.unfindable}</p>
              </div>
              <div className="flex items-center gap-3">
                <Car state="onHold" carNumber={carNumber} />
                <p className="mb-1 text-sm font-normal text-gray-800">{labels.legend.team.paused}</p>
              </div>
              <div className="flex items-center gap-3">
                <Car state="inReturn" carNumber={carNumber} />
                <p className="mb-1 text-sm font-normal text-gray-800">{labels.legend.team.returning}</p>
              </div>
              <div className="flex items-center gap-3">
                <Car state="onboardFlagged" carNumber={carNumber} />
                <p className="mb-1 w-32 text-sm font-normal text-gray-800">{labels.legend.team.onboardFlagged}</p>
              </div>
            </div>
          </div>

          <div className="max-w-[280px] space-y-5">
            <p className="text-base font-bold text-black">{labels.legend.request.title}</p>
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-3">
                <Pin state="pending" letter="A" />
                <p className="mb-1 text-sm font-normal text-gray-800">{labels.legend.request.pending}</p>
              </div>
              <div className="flex items-center gap-3">
                <Pin state="assigned" letter="A" />
                <p className="mb-1 text-sm font-normal text-gray-800">{labels.legend.request.assigned}</p>
              </div>
              <div className="flex items-center gap-3">
                <Pin state="allocated" letter="A" />
                <p className="mb-1 text-sm font-normal text-gray-800">{labels.legend.request.allocated}</p>
              </div>
              <div className="flex items-center gap-3">
                <Pin state="onboarded" letter="A" />
                <p className="mb-1 text-sm font-normal text-gray-800">{labels.legend.request.onboarded}</p>
              </div>
              <div className="flex items-center gap-3">
                <Pin state="unfindabled" letter="A" />
                <p className="mb-1 text-sm font-normal text-gray-800">{labels.legend.request.unfindable}</p>
              </div>
              <div className="mb-1 flex items-center gap-3">
                <Car state="completedSuccess" carNumber="3" />
                <p className="text-sm font-normal text-gray-800">{labels.legend.request.completed}</p>
              </div>
              <div className="mb-1 flex items-center gap-3">
                <Car state="completedFailure" carNumber="3" />
                <p className="text-sm font-normal text-gray-800">{labels.legend.request.cancelled}</p>
              </div>
              <div className="mb-1 ml-[3px] flex items-center gap-3">
                <CancelledStatus />
                <p className="ml-[3px] text-sm font-normal text-gray-800">{labels.legend.request.cancelled}</p>
              </div>
            </div>
          </div>

          <div className="flex max-w-[240px] flex-col gap-2.5">
            <div className="h-fit border-b border-gray-300 pb-7">
              <div className="px-5">
                <div className="space-y-5">
                  <p className="text-base font-bold text-black">{labels.legend.donation.title}</p>
                  <div className="flex flex-col gap-5">
                    <div className="flex flex-col gap-5">
                      <div className="flex items-center gap-3">
                        <Donation state="no" className="size-6" />
                        <p className="text-sm font-normal text-gray-800">{labels.legend.donation.no}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <Donation state="cash" className="size-6" />
                        <p className="text-sm font-normal text-gray-800">{labels.legend.donation.cash}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <Donation state="digital" className="size-6" />
                        <p className="text-sm font-normal text-gray-800">{labels.legend.donation.digital}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <Donation state="textSent" className="size-6" />
                        <p className="text-sm font-normal text-gray-800">{labels.legend.donation.textSent}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <Donation state="textClicked" className="size-6" />
                        <p className="text-sm font-normal text-gray-800">{labels.legend.donation.textClicked}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="px-5">
              <div className="space-y-5">
                <p className="text-base font-bold text-black">{labels.legend.teamMobileStatus.title}</p>
                <div className="flex flex-col gap-5">
                  <div className="flex flex-col gap-5">
                    <div className="flex items-center gap-3">
                      <MobileState state="noMobile" className="size-4 shrink-0" />
                      <p className="text-sm font-normal text-gray-800">{labels.legend.teamMobileStatus.noMobile}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <MobileState state="ok" className="size-4 shrink-0" />
                      <p className="text-sm font-normal text-gray-800">{labels.legend.teamMobileStatus.mobile}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <MobileState state="notConnected" className="size-4 shrink-0" />
                      <p className="text-sm font-normal text-gray-800">{labels.legend.teamMobileStatus.notConnected}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex max-w-[180px] flex-col gap-5">
            <div className="flex h-fit flex-col gap-5 border-b border-gray-300 pb-7">
              <p className="text-base font-bold text-black">{labels.legend.carVariations.title}</p>
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-3">
                  <Car state="empty" vip carNumber="" />
                  <p className="mb-1 text-sm font-normal text-gray-800">{labels.legend.carVariations.vip}</p>
                </div>
                <div className="flex items-center gap-3">
                  <Car state="available" carType="electric" carNumber="" />
                  <p className="mb-1 text-sm font-normal text-gray-800">{labels.legend.carVariations.electric}</p>
                </div>
                <div className="flex items-center gap-3">
                  <Car state="available" carType="manual" carNumber="" />
                  <p className="mb-1 text-sm font-normal text-gray-800">{labels.legend.carVariations.manual}</p>
                </div>
              </div>
            </div>

            <div className="flex h-fit flex-col gap-5 border-b border-gray-300 pb-7">
              <p className="text-base font-bold text-black">{labels.legend.automation.title}</p>
              <div className="flex flex-col gap-3">
                <div className="flex items-center gap-3">
                  <AutomatedStatus variant="active" className="shrink-0" />
                  <p className="texts-gray-800 text-sm font-normal">{labels.legend.automation.active}</p>
                </div>
                <div className="flex items-center gap-3">
                  <AutomatedStatus variant="pending" className="shrink-0" />
                  <p className="texts-gray-800 text-sm font-normal">{labels.legend.automation.pending}</p>
                </div>
                <div className="flex items-center gap-3">
                  <AutomatedStatus variant="inactive" className="shrink-0" />
                  <p className="texts-gray-800 text-sm font-normal">{labels.legend.automation.inactive}</p>
                </div>
              </div>
            </div>

            <div className="flex h-fit flex-col gap-5">
              <div className="flex flex-col gap-3">
                <div className="flex items-center gap-3">
                  <Icons.mobileRequest className="size-4 shrink-0 fill-blue-600" />
                  <p className="texts-gray-800 text-sm font-normal">{labels.legend.request.mobile}</p>
                </div>
              </div>
            </div>
          </div>
        </main>

        <footer className="flex flex-wrap justify-between gap-3 border border-gray-300 px-12 py-8 select-none">
          <div className="max-w-[160px] space-y-6">
            <Status status="pending" team={{ carNumber: '?' }} pin={{ letter }} />
            <p className="text-sm text-gray-800">{labels.legend.requestCardState.pending}</p>
          </div>
          <div className="max-w-[160px] space-y-6">
            <Status status="pending" team={{ carNumber }} pin={{ letter }} />
            <p className="text-sm text-gray-800">{labels.legend.requestCardState.automated}</p>
          </div>
          <div className="max-w-[160px] space-y-6">
            <Status status="assigned" team={{ carNumber }} pin={{ letter }} />
            <p className="text-sm text-gray-800">{labels.legend.requestCardState.assigned}</p>
          </div>
          <div className="max-w-[160px] space-y-6">
            <Status status="allocated" team={{ carNumber }} pin={{ letter }} />
            <p className="text-sm text-gray-800">{labels.legend.requestCardState.allocated}</p>
          </div>
          <div className="max-w-[160px] space-y-6">
            <Status status="onboarded" team={{ carNumber }} pin={{ letter: letterCompleted }} />
            <p className="text-sm text-gray-800">{labels.legend.requestCardState.onboarded}</p>
          </div>
        </footer>
      </DialogContent>
    </Dialog>
  )
)
